import { URL_DELIVERY_PHOTOS } from '@app/config/urls';
import { useBagContext } from '@app/contexts/Bag';
import { useRestaurantContext } from '@app/pages/Restaurant/context';
import { Complement, ComplementGroup } from '@app/types';
import {
  MenuItemFavorited,
  MenuItemFavoritedOfBag,
  MenuItemTypeGuard,
  MenuItemWithDetails,
  MenuItemWithDetailsOfBag,
} from '@app/types/menu-item';
import { calculateTotalItemPrice, shouldApplyCalculusByFlavourMoreExpensive } from '@app/utils/calculus';
import { useIsMobileScreenSize, useIsTabletOrMobileScreenSize } from '@app/utils/media-query';
import { getAmountComplementSelected } from '@app/utils/menu';
import { TagManager } from '@app/utils/tag-manager';
import { IconButton } from '@material-ui/core';
import FavoritedIcon from '@material-ui/icons/Favorite';
import FavoriteIcon from '@material-ui/icons/FavoriteBorder';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CloseButton from '../CloseButton';
import ModalResponsive from '../ModalResponsive';
import ShareButton from '../ShareButton';
import ShimmerImage from '../ShimmerImage';
import BillingResult from './BillingResult';
import ComplementGroupCard from './ComplementGroupCard';
import { MenuItemDetailsContext, MenuItemDetailsContextData } from './context';
import {
  createInvalidMessage as createMessageForInvalidComplementGroup,
  getInitialTotalItem,
  useComplementGroupsScroll,
  useComplementsGroupsValidation,
  useFavoritedState,
  useSaveInSession,
  ValidationArray,
} from './helpers';
import ObservationsInput from './ObservationsInput';
import SearchInput, { useSearchComplementsHandler } from './SearchInput';
import classes from './styles.module.css';
import ToastAmount, { ToastAmountRefAttrs } from './ToastAmount';
import { useAutoSelectRequiredComplements } from './use-auto-select-required-complements';

export enum ModalActionType {
  Adding,
  Editing,
}

type Props = {
  isVisible: boolean;
  menuItem: MenuItemWithDetails | MenuItemWithDetailsOfBag | MenuItemFavorited | MenuItemFavoritedOfBag;
  menuItemHasSessionDataInjected: boolean;
  onClose: () => void;
};

export default function MenuItemDetailsModal({ isVisible, menuItem, onClose, menuItemHasSessionDataInjected }: Props) {
  const bag = useBagContext();

  const { primaryColor, secondaryColor, tagManager } = useRestaurantContext();

  const toastAmountRef = useRef<ToastAmountRefAttrs>(null);

  const complementGroupsRefs = useRef<(HTMLElement | null)[]>([...Array(menuItem.gruposDeComplementos.length)]);

  const modalActionType = useMemo(() => {
    return MenuItemTypeGuard.isWithDetailsOfBag(menuItem) ? ModalActionType.Editing : ModalActionType.Adding;
  }, [menuItem]);

  const [totalItem, setTotalItem] = useState(
    getInitialTotalItem(menuItem, modalActionType, menuItemHasSessionDataInjected)
  );
  const [amountItem, setAmountItem] = useState(MenuItemTypeGuard.isWithDetailsOfBag(menuItem) ? menuItem.amount : 1);
  const [observations, setObservations] = useState(
    MenuItemTypeGuard.isWithDetailsOfBag(menuItem) ? menuItem.observations : ''
  );
  const [userTriedAddWithSomeGroupInvalid, setUserTriedAddWithSomeGroupInvalid] = useState(false);
  const { isFavorited, toggleIsFavorited } = useFavoritedState(menuItem.id);

  const session = useSaveInSession(menuItem.id);
  useEffect(() => session.updateAmount(amountItem), [amountItem]);

  const [
    validations,
    setValidations,
    shouldHighlightGroupStats,
    getInvalidComplementGroupIndex,
    existsSomeComplementGroupInvalid,
    revalidateAllGroups,
  ] = useComplementsGroupsValidation(menuItem.gruposDeComplementos);

  const containerRef = useRef<HTMLDivElement>(null);
  const complementGroupsBoxRef = useRef<HTMLDivElement>(null);

  useAutoSelectRequiredComplements({
    groups: menuItem.gruposDeComplementos,
    shouldAutoSelect: modalActionType === ModalActionType.Adding,
    onCalculated: () => {
      setTotalItem(calculateTotalItemPrice(menuItem));
      revalidateAllGroups();
    },
  });

  const scrollToHTMLElement = useComplementGroupsScroll();

  const isMobile = useIsMobileScreenSize();
  const isTabletOrMobile = useIsTabletOrMobileScreenSize();

  useEffect(() => {
    // FacebookPixel.logMenuItemViewed(menuItem);
    tagManager?.pushMenuItemViewed(menuItem);
  }, []);

  const { handler, wasNotFound, isSearching, amountOfResultsFound } = useSearchComplementsHandler(
    containerRef,
    complementGroupsBoxRef,
    isTabletOrMobile
  );

  function dispatchOnClose() {
    session.clear();
    onClose();
  }

  const renderComplementGroup = useCallback(
    (group: ComplementGroup, groupIndex: number) => {
      const isValid = Boolean(validations[groupIndex]);
      const isRequired = !group.opcional;

      return (
        <ComplementGroupCard
          key={group.id}
          group={group}
          isValid={isValid}
          onIsValidValueChange={handleIsValidValueChange}
          shouldHighlightError={shouldHighlightGroupStats[groupIndex]}
          shouldAnimateError={isRequired && !isValid && userTriedAddWithSomeGroupInvalid}
          index={groupIndex}
          onContainerRef={(ref) => (complementGroupsRefs.current[groupIndex] = ref)}
        />
      );

      function handleIsValidValueChange(newIsValidValue: boolean) {
        let nextValidationsArrayValue: ValidationArray;

        setValidations((previousValidations) => {
          previousValidations[groupIndex] = newIsValidValue;
          nextValidationsArrayValue = [...previousValidations];

          if (shouldScroll()) scrollToNextComplementGroup();

          return nextValidationsArrayValue;
        });

        function scrollToNextComplementGroup() {
          const complementGroupIndexToScroll = invalidComplementGroupIndex();

          if (complementGroupIndexToScroll === -1) setUserTriedAddWithSomeGroupInvalid(false);

          let complementGroupElement: HTMLElement | null = null;
          if (complementGroupIndexToScroll !== -1) {
            complementGroupElement = complementGroupsRefs.current[complementGroupIndexToScroll];
          } else if (groupIndex < menuItem.gruposDeComplementos.length - 1 && !group.opcional) {
            complementGroupElement = complementGroupsRefs.current[groupIndex + 1];
          }
          scrollToHTMLElement(complementGroupElement);
        }

        function shouldScroll() {
          const isRequiredAndSelectUntilTheMax = isRequired && getAmountComplementSelected(group) === group.maximo;
          const isFillingOptionalBeforeRequired = !isRequired && invalidComplementGroupIndex() < groupIndex;
          return newIsValidValue && (isRequiredAndSelectUntilTheMax || isFillingOptionalBeforeRequired);
        }

        function invalidComplementGroupIndex() {
          return getInvalidComplementGroupIndex(nextValidationsArrayValue);
        }
      }
    },
    [
      validations,
      modalActionType,
      getInvalidComplementGroupIndex,
      shouldHighlightGroupStats,
      userTriedAddWithSomeGroupInvalid,
    ]
  );

  const menuItemContextValue = useMemo((): MenuItemDetailsContextData => {
    const invalidComplementGroupIndex = getInvalidComplementGroupIndex();

    return {
      menuItem,
      total: totalItem,
      updateTotalFromComplementSelected,
      amount: amountItem,
      setAmount: setAmountItem,
      sumToTotalFromComplementPrice,
      subtractToTotalFromComplmentPrice,
      invalidComplementGroup:
        invalidComplementGroupIndex !== -1 ? menuItem.gruposDeComplementos[invalidComplementGroupIndex] : null,
      toastAmountRef: toastAmountRef,
    };

    function updateTotalFromComplementSelected(selected: Complement | null, previousSelected: Complement | null) {
      if (!selected) setTotalItem((total) => total - (previousSelected?.preco ?? 0));
      else setTotalItem((total) => total + selected.preco - (previousSelected?.preco ?? 0));

      if (previousSelected) session.removeGroup(previousSelected.grupoId);
      if (selected) session.updateComplement(selected);
    }

    function sumToTotalFromComplementPrice(complement: Complement, complementGroupParent: ComplementGroup) {
      if (menuItem.isPizzaItem && shouldApplyCalculusByFlavourMoreExpensive(complementGroupParent)) {
        setTotalItem(calculateTotalItemPrice(menuItem));
      } else {
        setTotalItem((oldValue) => oldValue + complement.preco);
      }
      session.updateComplement(complement);
    }

    function subtractToTotalFromComplmentPrice(complement: Complement, complementGroupParent: ComplementGroup) {
      if (menuItem.isPizzaItem && shouldApplyCalculusByFlavourMoreExpensive(complementGroupParent)) {
        setTotalItem(calculateTotalItemPrice(menuItem));
      } else {
        setTotalItem((oldValue) => oldValue - complement.preco);
      }
      session.updateComplement(complement);
    }
  }, [totalItem, amountItem, getInvalidComplementGroupIndex, menuItem, session]);

  function handleAddItemToCartClick() {
    if (existsSomeComplementGroupInvalid()) {
      const complementGroupIndexToScroll = getInvalidComplementGroupIndex();
      const complementGroupElement = document.querySelector(`#complement-group-${complementGroupIndexToScroll}`);
      const complementGroup = menuItem.gruposDeComplementos[complementGroupIndexToScroll];
      alert(createMessageForInvalidComplementGroup(menuItem, complementGroup));
      setTimeout(() => {
        scrollToHTMLElement(complementGroupElement as HTMLElement);
        setUserTriedAddWithSomeGroupInvalid(true);
      }, 200);
      return;
    }

    const extraData = {
      observations: observations.trim(),
      amount: amountItem,
    };

    if (modalActionType === ModalActionType.Editing) {
      const menuItemOfBag = menuItem as MenuItemWithDetailsOfBag;
      bag.updateItem(menuItemOfBag.bagItemId, menuItemOfBag, extraData);
    } else {
      bag.addItem(menuItem, extraData);
    }

    tagManager?.pushAddToBag(menuItem);

    dispatchOnClose();
  }

  function handleFavoriteButtonClick() {
    if (!isFavorited) tagManager?.pushAddToWishlist(menuItem);
    toggleIsFavorited();
  }

  const shouldDisplaySearchInput = Boolean((menuItem.gruposDeComplementos?.length ?? 0) > 0);

  return (
    <MenuItemDetailsContext.Provider value={menuItemContextValue}>
      <ModalResponsive
        open={isVisible}
        onClose={dispatchOnClose}
        appBarConfig={{ title: menuItem.menuName, color: primaryColor, tintColor: secondaryColor }}
        desktopWidthContent={910}
        desktopHeightContent={551}
        hiddenDesktopBar
        AppBarRightSide={
          <div className={classes.mobileModalBar}>
            <FavoriteButton color={secondaryColor} isFavorited={isFavorited} onClick={handleFavoriteButtonClick} />
            <ShareButton color={secondaryColor} />
          </div>
        }
      >
        <div className={classes.container} ref={containerRef}>
          <div className={classes.informationBox}>
            {!isMobile && <h2>{menuItem.nome}</h2>}
            {menuItem.foto && (
              <ShimmerImage
                useNativeImg
                src={URL_DELIVERY_PHOTOS + menuItem.foto}
                width="100%"
                height={isMobile ? 250 : 300}
                objectFit="contain"
                className={classes.menuItemPhoto}
              />
            )}
            {isMobile && <h2 style={{ marginTop: menuItem.foto ? 15 : 0 }}>{menuItem.nome}</h2>}
            {Boolean(menuItem.descricao) && <p>{menuItem.descricao}</p>}
          </div>
          <div className={classes.rightSideBox}>
            {!isTabletOrMobile && (
              <div className={classes.closeButtonBox}>
                <FavoriteButton
                  color="#777"
                  buttonSize="small"
                  isFavorited={isFavorited}
                  onClick={handleFavoriteButtonClick}
                />
                <ShareButton color="#777" buttonSize="small" iconSize="1.1em" />
                <CloseButton onClick={onClose} />
              </div>
            )}
            <div
              className={classes.complementGroupsBox}
              ref={complementGroupsBoxRef}
              style={{ paddingBottom: isTabletOrMobile ? 85 : 20 }}
            >
              {shouldDisplaySearchInput && (
                <SearchInput
                  onSearch={handler}
                  isSearching={isSearching}
                  wasNotFound={wasNotFound}
                  amountOfResultsFound={amountOfResultsFound}
                />
              )}
              {(menuItem?.gruposDeComplementos ?? []).map(renderComplementGroup)}
              {!menuItem.proibeObservacoes && (
                <ObservationsInput value={observations} onChangeValue={setObservations} />
              )}
            </div>
            <BillingResult
              onAddMenuItemClick={handleAddItemToCartClick}
              addItemButtonEnabled={!existsSomeComplementGroupInvalid()}
            />
          </div>
          <ToastAmount
            ref={toastAmountRef}
            parentElement={containerRef.current}
            color={primaryColor}
            tintColor={secondaryColor}
          />
        </div>
      </ModalResponsive>
    </MenuItemDetailsContext.Provider>
  );
}

type FavoriteButtonProps = {
  color?: string;
  buttonSize?: 'small' | 'medium';
  isFavorited: boolean;
  onClick: () => void;
};

function FavoriteButton({ color = '#fff', isFavorited, onClick, buttonSize = 'medium' }: FavoriteButtonProps) {
  const iconStyle = { color };
  const className = TagManager.Classes.AddToWishlist;

  return (
    <IconButton onClick={onClick} size={buttonSize} className={className}>
      {isFavorited ? (
        <FavoritedIcon style={iconStyle} className={className} />
      ) : (
        <FavoriteIcon style={iconStyle} className={className} />
      )}
    </IconButton>
  );
}
